import React, { useState } from 'react';
import { Box, Stack, Paper, Button, Divider, Badge, useMediaQuery, useTheme } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GavelIcon from '@mui/icons-material/Gavel';
import PieChartIcon from '@mui/icons-material/PieChart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterDropdown from './FilterDropdown';
import BudgetFilterContent from './BudgetFilterContent';
import {
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_1_300,
  COLOR_PRIMARY_2,
  COLOR_PRIMARY_2_600,
} from 'src/colors';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';

// Define common styles for all buttons
const commonButtonSx = {
  justifyContent: 'flex-start',
  boxShadow: 'none',
};

// Define styles specific to the contained (active) state
const containedButtonSx = {
  backgroundColor: COLOR_PRIMARY_2_600,
  color: 'white',
  borderColor: COLOR_PRIMARY_2,
  boxShadow: 1,
  '&:hover': {
    backgroundColor: COLOR_PRIMARY_2_600,
    borderColor: COLOR_PRIMARY_2_600,
    boxShadow: 3,
  },
};

// Define styles specific to the outlined (inactive) state
const outlinedButtonSx = {
  borderColor: COLOR_PRIMARY_2,
  color: COLOR_PRIMARY_2,
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(66, 85, 99, 0.04)',
    borderColor: COLOR_PRIMARY_2,
    color: COLOR_PRIMARY_2,
  },
};

// Define Props Interface
interface OptionType {
  value: string;
  label: string;
  isState?: boolean;
  countryCode?: string;
}

interface FilterBarProps {
  selectedRegions: string[];
  regionOptions: OptionType[];
  handleRegionsChange: (val: string[]) => void;
  selectedLocations: string[];
  selectedRegulations: string[];
  selectedMarketSizes: string[];
  selectedBudget?: {
    buy?: { min: number; max: number };
    rent?: { min: number; max: number };
  };
  selectedBudgetMode: 'buy' | 'rent';
  handleLocationsChange: (val: string[]) => void;
  handleRegulationsChange: (val: string[] | string) => void;
  handleMarketSizesChange: (val: string[] | string) => void;
  handleBudgetChange: (budgetType: string, values: { min: number; max: number }) => void;
  handleBudgetModeChange: (mode: 'buy' | 'rent') => void;
  countryOptions: OptionType[];
  regulationOptions: OptionType[];
  marketSizeOptions: OptionType[];
  onFilterChangeAccessCheckMiddleware: (val: string | string[], actionFunction: any) => void;
  openSearchModal: () => void;
  isMobileScreen: boolean;
  OnBoardingSearchBox: any;
  setMarketSearchInfo: (val: { searchName: string; countryCode: string } | null) => void;
  setMarketComparisons: any;
  isFreeUser: boolean;
  currency: string;
  handleSidebarClick?: any;
  handleSidebarListItemClick?: any;
  onExport: () => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  selectedRegions,
  handleRegionsChange,
  selectedLocations,
  selectedRegulations,
  selectedMarketSizes,
  selectedBudget,
  selectedBudgetMode,
  handleLocationsChange,
  handleRegulationsChange,
  handleMarketSizesChange,
  handleBudgetChange,
  handleBudgetModeChange,
  countryOptions,
  regulationOptions,
  marketSizeOptions,
  onFilterChangeAccessCheckMiddleware,
  openSearchModal,
  isMobileScreen,
  OnBoardingSearchBox,
  setMarketSearchInfo,
  setMarketComparisons,
  isFreeUser,
  currency,
  handleSidebarClick,
  handleSidebarListItemClick,
  onExport,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const ExportSection = () => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: isMobile ? 'flex-start' : 'flex-end', my: '10px' }}>
        <Button
          variant="outlined"
          onClick={onExport}
          startIcon={<FileDownloadOutlinedIcon />}
          sx={{
            background: COLOR_PRIMARY_1_300 + '22',
            fontWeight: 600,
            height: '40px',
            borderColor: 'rgba(0, 0, 0, 0.23)',
          }}
        >
          Export
        </Button>

        {/* <Button
          variant="outlined"
          // onClick={}
          startIcon={<ViewWeekOutlinedIcon />}
          sx={{
            background: COLOR_PRIMARY_1_300 + '22',
            fontWeight: 600,
            height: '40px',
            borderColor: 'rgba(0, 0, 0, 0.23)',
          }}
        >
          Customize Columns
        </Button> */}
      </Box>
    );
  };

  return (
    <>
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        overflowX: 'auto',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        bgcolor: 'background.paper',
        pt: '5px',
      }}
    >
      <Box sx={{ pr: 2, py: 1, minWidth: 'max-content' }}>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <FilterDropdown
              label="Location"
              button={
                <Button
                  variant={selectedLocations.length > 0 ? 'contained' : 'outlined'}
                  startIcon={<LocationOnIcon />}
                  endIcon={<ExpandMoreIcon />}
                  sx={{
                    ...commonButtonSx,
                    ...(selectedLocations.length > 0 ? containedButtonSx : outlinedButtonSx),
                  }}
                  disableElevation
                >
                  Location
                </Button>
              }
              options={countryOptions}
              value={selectedLocations}
              onChange={val => {
                handleLocationsChange(val as string[]);
                if (setMarketSearchInfo) {
                  setMarketSearchInfo(null);
                }
              }}
              multiSelect
              selectedCountBadge
              placeholder="Search country or US state"
              onNoResultsAction={openSearchModal}
              OnBoardingSearchBox={OnBoardingSearchBox}
              setMarketSearchInfo={setMarketSearchInfo}
              setMarketComparisons={setMarketComparisons}
              isFreeUser={isFreeUser}
            />
            <FilterDropdown
              label="Regulation"
              button={
                <Button
                  variant={selectedRegulations.length > 0 ? 'contained' : 'outlined'}
                  startIcon={<GavelIcon />}
                  endIcon={<ExpandMoreIcon />}
                  sx={{
                    ...commonButtonSx,
                    ...(selectedRegulations.length > 0 ? containedButtonSx : outlinedButtonSx),
                  }}
                  disableElevation
                >
                  Regulation
                </Button>
              }
              options={regulationOptions}
              value={selectedRegulations}
              onChange={val => onFilterChangeAccessCheckMiddleware(val, handleRegulationsChange)}
              multiSelect
              selectedCountBadge
              placeholder="Search regulation"
              hideSearch={true}
              isFreeUser={isFreeUser}
              rowGapSize="big"
            />

            <FilterDropdown
              label="Budget"
              button={
                <Button
                  variant={'contained'}
                  startIcon={<AccountBalanceWalletIcon />}
                  endIcon={<ExpandMoreIcon />}
                  sx={{
                    ...commonButtonSx,
                    ...containedButtonSx,
                  }}
                  disableElevation
                >
                  <>
                    Budget (<span style={{ textTransform: 'capitalize' }}>{selectedBudgetMode}</span>)
                  </>
                </Button>
              }
              options={[]}
              value={[]}
              onChange={() => {}}
              customContent={({ handleClose }) => (
                <BudgetFilterContent
                  onBudgetChange={handleBudgetChange}
                  initialBudget={selectedBudget}
                  mode={selectedBudgetMode}
                  onModeChange={handleBudgetModeChange}
                  currency={currency}
                  onClose={handleClose}
                />
              )}
              hideSearch={true}
            />

            <FilterDropdown
              label="Market Size"
              rowGapSize="big"
              button={
                <Button
                  variant={selectedMarketSizes.length > 0 ? 'contained' : 'outlined'}
                  startIcon={<PieChartIcon />}
                  endIcon={<ExpandMoreIcon />}
                  sx={{
                    ...commonButtonSx,
                    ...(selectedMarketSizes.length > 0 ? containedButtonSx : outlinedButtonSx),
                  }}
                  disableElevation
                >
                  Market Size
                </Button>
              }
              options={marketSizeOptions}
              value={selectedMarketSizes}
              onChange={val => onFilterChangeAccessCheckMiddleware(val, handleMarketSizesChange)}
              multiSelect
              selectedCountBadge
              placeholder="Search market size"
              hideSearch={true}
              isFreeUser={isFreeUser}
            />
          </Stack>
          {!isMobile && ExportSection()}
        </Stack>
      </Box>
    </Paper>
        {isMobile && ExportSection()}
        </>
  );
};

export default FilterBar;
